// Thin wrappers around the browser's Web Speech API.
// SpeechSynthesis (TTS) is well-supported everywhere.
// SpeechRecognition (mic STT) ships in Chrome/Edge/Safari (no Firefox).

const w = typeof window !== "undefined" ? window : {};

export const TTS_SUPPORTED =
  typeof w !== "undefined" &&
  typeof w.speechSynthesis !== "undefined" &&
  typeof w.SpeechSynthesisUtterance !== "undefined";

export const STT_SUPPORTED =
  typeof w !== "undefined" &&
  (typeof w.SpeechRecognition !== "undefined" ||
    typeof w.webkitSpeechRecognition !== "undefined");

// Voice list takes a tick to populate in some browsers, hence the promise.
export function getVoices() {
  return new Promise((resolve) => {
    if (!TTS_SUPPORTED) return resolve([]);
    const initial = w.speechSynthesis.getVoices();
    if (initial && initial.length) return resolve(initial);
    w.speechSynthesis.onvoiceschanged = () =>
      resolve(w.speechSynthesis.getVoices());
  });
}

/**
 * Speak text and resolve when finished.
 * Returns the utterance so the caller can cancel it.
 */
export function speak(text, { voice, rate = 1, pitch = 1, volume = 1 } = {}) {
  if (!TTS_SUPPORTED) {
    return Promise.reject(new Error("Speech synthesis not supported"));
  }
  return new Promise((resolve, reject) => {
    const u = new SpeechSynthesisUtterance(text);
    if (voice) u.voice = voice;
    u.rate = rate;
    u.pitch = pitch;
    u.volume = volume;
    u.onend = () => resolve(u);
    u.onerror = (e) => reject(e.error || new Error("TTS error"));
    w.speechSynthesis.cancel();
    w.speechSynthesis.speak(u);
  });
}

export function cancelSpeech() {
  if (TTS_SUPPORTED) w.speechSynthesis.cancel();
}

/**
 * Start streaming mic transcription.
 * `onResult({ text, isFinal })` fires for each interim/final result.
 * Returns a `stop()` function.
 */
export function startRecognition({ lang = "en-US", onResult, onError } = {}) {
  if (!STT_SUPPORTED) {
    onError?.(new Error("Speech recognition not supported in this browser"));
    return () => {};
  }
  const SR = w.SpeechRecognition || w.webkitSpeechRecognition;
  const rec = new SR();
  rec.lang = lang;
  rec.continuous = true;
  rec.interimResults = true;
  rec.onresult = (event) => {
    let interim = "";
    let final = "";
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const r = event.results[i];
      if (r.isFinal) final += r[0].transcript;
      else interim += r[0].transcript;
    }
    onResult?.({ text: (final || interim).trim(), isFinal: Boolean(final) });
  };
  rec.onerror = (e) => onError?.(e.error || e);
  rec.start();
  return () => {
    try {
      rec.stop();
    } catch {}
  };
}

/**
 * Convert a Blob (e.g. recorded audio) into a base64 data URL.
 */
export function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

/**
 * Record audio from the user's microphone using MediaRecorder.
 * Returns { stop() } where stop returns the recorded Blob.
 */
export async function recordMic({ mimeType = "audio/webm" } = {}) {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const recorder = new MediaRecorder(stream, { mimeType });
  const chunks = [];
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size) chunks.push(e.data);
  };
  recorder.start();
  return {
    stop() {
      return new Promise((resolve) => {
        recorder.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          resolve(new Blob(chunks, { type: mimeType }));
        };
        recorder.stop();
      });
    },
    pause() {
      recorder.pause();
    },
    resume() {
      recorder.resume();
    },
    stream,
  };
}
